import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Form, FormSpy } from 'react-final-form';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { FormContainer } from '@spark/ui-components/components/form/FormElements';
import { descargaFormularioSections, descargaFormularioStructure } from './config';

const getValue = (record, key) => {
  const value = record?.[key];
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const isEmptyDisplayValue = (value) => value === null || value === undefined || value === '' || value === '-';

const toDateTimeLocalValue = (value) => {
  if (!value || value === '-') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const buildInitialValues = (descarga) => ({
  numeroCarga: getValue(descarga, 'numeroCarga'),
  protocoloCarga: getValue(descarga, 'protocoloCarga'),
  codigoCarga: getValue(descarga, 'codigoCarga'),
  situacaoCarga: getValue(descarga, 'situacaoCarga'),
  dataCriacaoCarga: getValue(descarga, 'dataCriacaoCarga'),
  numeroPedido: getValue(descarga, 'numeroPedido'),
  protocoloPedido: getValue(descarga, 'protocoloPedido'),
  codigoPedido: getValue(descarga, 'codigoPedido'),
  dataAgendamentoPedido: getValue(descarga, 'dataAgendamentoPedido'),
  dataCriacaoPedido: getValue(descarga, 'dataCriacaoPedido'),
  dataCarregamentoPedido: getValue(descarga, 'dataCarregamentoPedido'),
  placaVeiculo: getValue(descarga, 'placaVeiculo'),
  numeroFrota: getValue(descarga, 'numeroFrota'),
  descricaoModeloVeicular: getValue(descarga, 'descricaoModeloVeicular'),
  placasReboque: getValue(descarga, 'placasReboque'),
  frotasReboque: getValue(descarga, 'frotasReboque'),
  modelosReboque: getValue(descarga, 'modelosReboque'),
  infoMotorista: getValue(descarga, 'infoMotorista'),
  nomeRemetente: getValue(descarga, 'nomeRemetente'),
  cnpjRemetente: getValue(descarga, 'cnpjRemetente'),
  nomeDestinatario: getValue(descarga, 'nomeDestinatario'),
  cnpjDestinatario: getValue(descarga, 'cnpjDestinatario'),
  loja: getValue(descarga, 'loja'),
  nomeTomador: getValue(descarga, 'nomeTomador'),
  cnpjTomador: getValue(descarga, 'cnpjTomador'),
  nomeResponsavel: getValue(descarga, 'nomeResponsavel'),
  dataAgendamento: toDateTimeLocalValue(descarga?.dataAgendamentoDescargaOriginal),
  chegada: toDateTimeLocalValue(descarga?.chegadaDescargaOriginal),
  saida: toDateTimeLocalValue(descarga?.saidaDescargaOriginal),
  descricaoPosicaoAngellira: getValue(descarga, 'descricaoPosicaoAngellira'),
  codigoViagemAngellira: getValue(descarga, 'codigoViagemAngellira'),
  statusViagemAngellira: getValue(descarga, 'statusViagemAngellira'),
  clienteDistanciaAngellira: getValue(descarga, 'clienteDistanciaAngellira'),
  clienteTempoPrevChegadaAngellira: getValue(descarga, 'clienteTempoPrevChegadaAngellira'),
  observacoes: isEmptyDisplayValue(descarga?.observacoesDescargaOriginal ?? descarga?.observacoesDescarga)
    ? ''
    : String(descarga?.observacoesDescargaOriginal ?? descarga?.observacoesDescarga)
});

const DescargaFormulario = ({ descarga, onValuesChange, readOnly }) => {
  const initialValues = useMemo(() => buildInitialValues(descarga), [descarga]);
  const structure = useMemo(() => {
    if (!readOnly) return descargaFormularioStructure;

    return descargaFormularioStructure.map(row => row.map(field => ({
      ...field,
      disabled: true
    })));
  }, [readOnly]);

  return (
    <Form
      onSubmit={() => undefined}
      initialValues={initialValues}
      keepDirtyOnReinitialize={false}
    >
      {({ handleSubmit }) => (
        <FormContainer onSubmit={handleSubmit}>
          <DynamicForm
            structure={structure}
            sections={descargaFormularioSections}
            isViewMode={readOnly}
          />
          {onValuesChange ? (
            <FormSpy
              subscription={{ values: true }}
              onChange={({ values }) => {
                onValuesChange(values);
              }}
            />
          ) : null}
        </FormContainer>
      )}
    </Form>
  );
};

DescargaFormulario.propTypes = {
  descarga: PropTypes.shape(),
  onValuesChange: PropTypes.func,
  readOnly: PropTypes.bool
};

DescargaFormulario.defaultProps = {
  descarga: null,
  onValuesChange: null,
  readOnly: false
};

export default DescargaFormulario;
